// src/components/Onboarding/PhonePreview/PhonePreview.js
import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * PhonePreview
 * Renders an iPhone frame (SVG/PNG) with a masked screen area that holds a vertical video.
 *
 * Props:
 * - frameSrc        : required (SVG/PNG)
 * - aspectRatio     : string like "1294/2656" (default uses iPhone 15 Pro frame)
 * - screenRect      : { xPercent, yPercent, widthPercent, heightPercent }
 * - videoMp4        : optional video source
 * - videoWebm       : optional webm source
 * - poster          : optional poster image (recommended)
 * - autoplay/muted/loop/playsInline: standard video flags (defaults set for autoplay to work)
 * - className       : optional wrapper classes (centering/sizing set here)
 * - style           : optional inline style overrides
 */
export default function PhonePreview({
  frameSrc,
  aspectRatio = "1294/2656", // width/height for CSS aspect-ratio
  screenRect = {
    xPercent: 4.0093,
    yPercent: 0.7154,
    widthPercent: 91.9042,
    heightPercent: 98.5693,
  },
  videoMp4,
  videoWebm,
  poster,
  autoplay = true,
  muted = true,
  loop = true,
  playsInline = true,
  className = "",
  style = {},
  onVideoReady,
  onVideoError,
}) {
  const { xPercent, yPercent, widthPercent, heightPercent } = screenRect;
  const videoContainerRef = useRef(null);
  const videoRef = useRef(null);
  const onVideoReadyRef = useRef(onVideoReady);
  const onVideoErrorRef = useRef(onVideoError);
  const [fallbackToPoster, setFallbackToPoster] = useState(false);

  const isSafari = useMemo(() => detectSafari(), []);
  useEffect(() => {
    onVideoReadyRef.current = onVideoReady;
  }, [onVideoReady]);

  useEffect(() => {
    onVideoErrorRef.current = onVideoError;
  }, [onVideoError]);

  const sourceKey = useMemo(
    () => `${videoMp4 ?? ""}|${videoWebm ?? ""}`,
    [videoMp4, videoWebm],
  );
  const previousSourceKeyRef = useRef(null);

  // Reset poster fallback when source changes
  useEffect(() => {
    setFallbackToPoster(false);
  }, [sourceKey]);

  useEffect(() => {
    const sourceChanged = previousSourceKeyRef.current !== sourceKey;
    previousSourceKeyRef.current = sourceKey;
    // no need for play attempt sentinels; original logic handles retries gracefully

    const wrapper = videoContainerRef.current;
    const videoEl = videoRef.current;
    if (!videoEl) {
      return undefined;
    }

    const applyAttributes = () => {
      videoEl.autoplay = Boolean(autoplay);
      videoEl.loop = Boolean(loop);
      videoEl.controls = false;
      videoEl.defaultMuted = Boolean(muted);
      videoEl.muted = Boolean(muted);

      if (muted) {
        videoEl.setAttribute("muted", "");
      } else {
        videoEl.removeAttribute("muted");
      }

      if (playsInline) {
        videoEl.playsInline = true;
        videoEl.setAttribute("playsinline", "");
        videoEl.setAttribute("webkit-playsinline", "");
      } else {
        videoEl.playsInline = false;
        videoEl.removeAttribute("playsinline");
        videoEl.removeAttribute("webkit-playsinline");
      }

      const preloadMode = autoplay ? "auto" : "metadata";
      videoEl.setAttribute("preload", preloadMode);

      // Keep poster in sync
      if (poster) {
        try { videoEl.poster = poster; } catch (_) {}
      } else {
        try { videoEl.removeAttribute("poster"); } catch (_) {}
      }
    };

    const attemptPlay = () => {
      if (!autoplay) return;
      try {
        const maybePromise = videoEl.play();
        if (maybePromise && typeof maybePromise.catch === 'function') {
          maybePromise.catch(() => {
            if (isSafari && poster) {
              setFallbackToPoster(true);
            }
          });
        }
      } catch (error) {
        if (isSafari && poster) {
          setFallbackToPoster(true);
        }
      }
    };

    applyAttributes();

    // Update source without re-creating the <video> element
    if (sourceChanged) {
      try {
        // Prefer MP4 if provided; else fall back to WebM
        const nextSrc = videoMp4 || videoWebm || "";
        if (nextSrc) {
          // Only update when different to avoid implicit resets
          if (videoEl.currentSrc !== nextSrc && videoEl.src !== nextSrc) {
            videoEl.src = nextSrc;
          }
        }
      } catch (_) {
        // ignore
      }
    }


    if (autoplay && isSafari) {
      // Only programmatically trigger autoplay on Safari/iOS
      attemptPlay();
    }

    const handleLoadedData = () => {
      if (autoplay && isSafari) {
        attemptPlay();
      }
    };

    const handleCanPlay = () => {
      if (autoplay && isSafari) {
        attemptPlay();
      }
    };

    const handlePlay = () => {
      setFallbackToPoster(false);
      if (onVideoReadyRef.current) {
        try {
          onVideoReadyRef.current(videoEl);
        } catch (_) {
          // ignore callback errors
        }
      }
    };

    const handleError = (event) => {
      if (onVideoErrorRef.current) {
        try {
          onVideoErrorRef.current(event);
        } catch (_) {
          // ignore callback errors
        }
      }
    };

    // Only attach autoplay helpers for Safari
    if (isSafari) {
      videoEl.addEventListener("loadeddata", handleLoadedData);
      videoEl.addEventListener("canplay", handleCanPlay);
      videoEl.addEventListener("canplaythrough", handleCanPlay);
    }
    videoEl.addEventListener("play", handlePlay);
    videoEl.addEventListener("error", handleError);

    return () => {
      if (isSafari) {
        videoEl.removeEventListener("loadeddata", handleLoadedData);
        videoEl.removeEventListener("canplay", handleCanPlay);
        videoEl.removeEventListener("canplaythrough", handleCanPlay);
      }
      videoEl.removeEventListener("play", handlePlay);
      videoEl.removeEventListener("error", handleError);
    };
  }, [autoplay, isSafari, loop, muted, playsInline, poster, sourceKey, videoMp4, videoWebm]);

  const shouldRenderVideo = Boolean(videoMp4 || videoWebm) && !fallbackToPoster;

  return (
    <div
      className={[
        "relative w-full max-w-full isolate",
        "rounded-[28px] overflow-hidden",
        className,
      ].join(" ")}
      style={{
        aspectRatio,
        ...style,
      }}
      aria-label="Phone preview"
    >
      {/* Screen (video) layer */}
      <div
        className="absolute z-[1] overflow-hidden [transform:translateZ(0)]"
        style={{
          left: `${xPercent}%`,
          top: `${yPercent}%`,
          width: `${widthPercent}%`,
          height: `${heightPercent}%`,
          WebkitMaskImage: "radial-gradient(#fff,#fff)",
          contain: "paint",
          borderRadius: "0px",
        }}
        aria-hidden="true"
      >
        {shouldRenderVideo ? (
          <div
            ref={videoContainerRef}
            className="w-full h-full"
            style={{
              clipPath: "inset(0 round 22px)",
              borderRadius: "22px",
              WebkitTransform: "translateZ(0)",
              transform: "translateZ(0)",
            }}
          >
            <video
              ref={videoRef}
              className="w-full h-full object-cover block"
              playsInline
              muted
              loop
            />
          </div>
        ) : poster ? (
          <img
            src={poster}
            alt=""
            className="w-full h-full object-cover"
            style={{
              clipPath: "inset(0 round 22px)",
              borderRadius: "22px",
            }}
            draggable={false}
          />
        ) : (
          <div className="w-full h-full">
            <div className="w-full h-full bg-gradient-to-b from-gray-200 to-gray-300" />
          </div>
        )}
      </div>

      {/* Frame artwork above the screen */}
      <img
        src={frameSrc}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full pointer-events-none select-none z-[2]"
        draggable={false}
      />
    </div>
  );
}

function detectSafari() {
  if (typeof navigator === "undefined" || !navigator.userAgent) {
    return false;
  }
  const ua = navigator.userAgent.toLowerCase();
  const isSafariEngine = ua.includes("safari");
  const isOtherBrowser =
    ua.includes("chrome") ||
    ua.includes("crios") ||
    ua.includes("android") ||
    ua.includes("fxios") ||
    ua.includes("firefox") ||
    ua.includes("edg");
  return isSafariEngine && !isOtherBrowser;
}

function escapeAttribute(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
